import { createContext, useContext, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Icon } from '../../components/Icon';
import { Button } from '../../components/ui';
import { LIVE_BORDER, LIVE_FILL, LIVE_FOCUS, LIVE_SOFT } from './liveStyles';

/**
 * Explicit confirmation before any Live route renders.
 *
 * The acknowledgement lives in React state for this page load only — deliberately not
 * localStorage or sessionStorage: it must be re-given after a reload, and Milestone 3
 * tightens it to re-gate on every entry into the Live branch from outside it.
 */
interface LiveSessionValue {
  acknowledged: boolean;
  acknowledge: () => void;
  revoke: () => void;
}

const LiveSessionContext = createContext<LiveSessionValue | null>(null);

export function LiveSessionProvider({ children }: { children: ReactNode }) {
  const [acknowledged, setAcknowledged] = useState(false);
  return (
    <LiveSessionContext.Provider
      value={{
        acknowledged,
        acknowledge: () => setAcknowledged(true),
        revoke: () => setAcknowledged(false),
      }}
    >
      {children}
    </LiveSessionContext.Provider>
  );
}

export function useLiveSession(): LiveSessionValue {
  const ctx = useContext(LiveSessionContext);
  if (!ctx) throw new Error('useLiveSession must be used inside <LiveSessionProvider>');
  return ctx;
}

export function LiveGate({ children }: { children: ReactNode }) {
  const { acknowledged, acknowledge } = useLiveSession();
  const navigate = useNavigate();
  if (acknowledged) return <>{children}</>;

  return (
    <div className="mx-auto max-w-[560px] px-4 py-10" data-live-gate>
      <section
        aria-labelledby="live-gate-title"
        className={`overflow-hidden rounded-panel border-2 bg-surface ${LIVE_BORDER}`}
      >
        <header className={`flex items-center gap-2.5 px-4 py-3 ${LIVE_FILL}`}>
          <Icon name="alert" size={18} />
          <h1 id="live-gate-title" className="m-0 text-[15px] font-semibold tracking-wide">
            You are entering LIVE trading
          </h1>
        </header>
        <div className="space-y-3 px-4 py-4 text-[13px] text-ink-2">
          <p className="m-0">
            Everything under <strong className="text-ink">Trade → Live</strong> refers to a real
            brokerage account and real money. Paper trading is a different branch with a different
            colour and never shares a screen with this one.
          </p>
          <p className="m-0">
            In Milestone 2 nothing here can route an order. The confirmation exists now so the habit
            is in place before there is money behind it.
          </p>
          <p className={`m-0 rounded-panel px-3 py-2 text-[12.5px] ${LIVE_SOFT}`}>
            The banner and the LIVE badge stay on screen for as long as you are in this branch.
          </p>
        </div>
        <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-raised px-4 py-3">
          <Button type="button" variant="ghost" onClick={() => navigate('/trade/paper/positions')}>
            Back to Paper
          </Button>
          <button
            type="button"
            data-live-confirm
            onClick={acknowledge}
            className={`inline-flex h-[30px] items-center gap-1.5 rounded-panel px-3 text-[12.5px] font-semibold ${LIVE_FILL} ${LIVE_FOCUS} hover:brightness-110`}
          >
            <Icon name="alert" size={14} />I understand — enter Live
          </button>
        </footer>
      </section>
    </div>
  );
}
